import React, { FC } from 'react'
import Head from 'next/head'

import { localeToCountryCode } from '@app/utils/i18n'
import { getLinkFromHrefLang } from '@app/utils/hreflang-utils'
import { HrefLangAlternate } from '@app/services/href-lang-service'

const shouldIndexPage = (value: string) =>
  value === 'index, follow' || value === 'index, nofollow'

type Props = {
  title: string
  description: string
  robots: string
  keywords: string
  pageData: string
  canonical: string
  countryCode: string
  urls: { [key: string]: string }
  availableCountries: string[]
  hrefLangTags: string[]
}

export const Seo: FC<Props> = ({
  title,
  description,
  robots,
  keywords,
  pageData,
  canonical,
  countryCode,
  urls = {},
  availableCountries = [],
  hrefLangTags = [],
}) => {
  const canonicalUrl = canonical ? canonical : urls[countryCode]
  const isSelfReferencingCanonical = canonical
    ? canonical.includes(urls[countryCode])
    : true
  const shouldRenderHrefLang =
    isSelfReferencingCanonical &&
    shouldIndexPage(robots) &&
    availableCountries.length > 1

  // this is used by the Storyblok search crawler
  const searchMetadata = pageData ? `,$$${pageData}$$` : ''

  return (
    <Head>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={robots} />
      <meta name="keywords" content={`${keywords ?? ''}${searchMetadata}`} />

      <link
        rel="canonical"
        href={`${process.env.NEXT_PUBLIC_BASE_URL}${canonicalUrl}`}
      />

      {shouldRenderHrefLang &&
        hrefLangTags.map((item: string | HrefLangAlternate) => {
          let countryCode
          let url
          // Support to use default available countries if there no value on the plugin
          if (typeof item === 'string') {
            const urlCountryCode = localeToCountryCode(item)
            countryCode = item
            url = `${process.env.NEXT_PUBLIC_BASE_URL}${urls[urlCountryCode]}`
          } else {
            // It comes from the plugin
            countryCode = item.locale
            url = getLinkFromHrefLang(item.url)
          }

          return (
            <link
              key={countryCode}
              rel="alternate"
              hrefLang={countryCode}
              href={url}
            />
          )
        })}
    </Head>
  )
}
